import styles from '../DashboardReview/styles.module.css'
import React, { useEffect } from 'react'
import { useDashboardReview } from '../../hooks/useDashboardReview'
import { ReviewFilter, ReviewFilterBadges } from '../DashboardReview/ReviewFilter'
import { ContextNavigation } from '../DashboardReview/ContextNavigation'
import { Helmet } from 'react-helmet'
import { findScopedResult, isResultCompleted } from '../../model/results'
import { useSelector } from 'react-redux'
import { RootState, Subtask } from 'constants/interfaces'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button, Chip } from 'components/ui'
import { isRtlSelector } from 'redux/selectors/general'
import { TaskOverviewContentType, useTaskOverviewContext } from '../../TaskOverviewStackProvider'
import { useMediaSubtaskPager } from '../../useMediaSubtaskPager'
import { useExecutionText } from '../../hooks/useExecutionText'
import { ExecutionSubtaskNavigation } from '../ExecutionSubtaskNavigation'
import { ExecutionStatus } from '../ExecutionStatus'
import { getPriority } from '../../utils'
import { PRIORITY_VALUES } from '../../../TaskCreation/utils'
import { useTranslateDefaultTitle } from 'components/TasksV2/hooks/useTranslateDefaultTitle'

export const ExecutionHeader: React.FC<{
  title: string
  subtask?: Subtask
  actionsRef?: React.Ref<HTMLDivElement>
  navigationRef?: React.Ref<HTMLDivElement>
}> = ({ title, subtask, actionsRef, navigationRef }) => {
  const { task, closeContent, isReadMode, resultContextId, contentStack } = useTaskOverviewContext()
  const returnToOverview = !!subtask && contentStack.some((frame) => frame.contentType === TaskOverviewContentType.MAIN)
  const review = useDashboardReview()
  const pager = useMediaSubtaskPager()
  const t = useExecutionText()
  const taskSubtasks = useSelector((s: RootState) => s.tasks_v2.subtasks[task.tid] || [])
  const results = useSelector((s: RootState) => s.tasks_v2.allResults[task.tid] || [])
  const countedSubtasks = review?.subtasks || taskSubtasks
  const completed = countedSubtasks.filter((subtask) =>
    isResultCompleted(findScopedResult(results, subtask.st_id, resultContextId, task.recurrence_ts))
  ).length
  const isRtl = useSelector(isRtlSelector)
  const { retailUsersObject, groups } = useSelector((s: RootState) => s.config.config)
  const author = retailUsersObject?.[task.created_by]
  const recipient = retailUsersObject?.[resultContextId]
  const name = (user: typeof author) => (user ? `${user.first_name} ${user.last_name || ''}`.trim() : '')
  const subtitle = `${review && subtask ? task.title : name(author) || 'STOREE'} › ${
    groups?.[resultContextId]?.name || name(recipient) || ''
  }`
  const translatedTitle = useTranslateDefaultTitle(title || '')
  const priority =
    task.priority != null && task.priority !== PRIORITY_VALUES.NONE ? getPriority(task.priority) : undefined
  useEffect(() => {
    if (review && subtask) review.setSelected(subtask.st_id, true)
  }, [review, subtask])

  return (
    <div className={`execution-header ${review ? styles.header : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
      <Helmet>
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Noto+Sans+Hebrew:wght@400;500;600;700&family=Public+Sans:wght@400;500;600;700&display=swap"
        />
      </Helmet>
      <Button
        variant="ghost"
        className="execution-back"
        onClick={closeContent}
        isDisabled={pager.isBusy}
        aria-label={returnToOverview ? t.allSubtasks : t.back}
      >
        {isRtl ? <ChevronRight size={19} /> : <ChevronLeft size={19} />}
      </Button>
      <div className="execution-header-title">
        <strong title={translatedTitle}>{translatedTitle}</strong>
        <small>{subtitle}</small>
      </div>
      <div className={`execution-header-meta ${review && subtask ? styles.subtaskMeta : ''}`}>
        {subtask?.category && (
          <Chip
            label={<>{subtask.category}</>}
            size="md"
            radius="7px"
            color="#ffffff"
            bg="#22272a"
            border="1px solid #22272a"
          />
        )}
        {subtask?.is_ai_enhanced && (
          <Chip label={<>✦ {t.aiSubtask}</>} size="md" radius="7px" color="#6b4fa8" bg="#efe9fb" border="0" />
        )}
        {isReadMode && subtask ? (
          <ExecutionStatus size="md" status={pager.status ?? undefined} approval={task.is_required_approval} />
        ) : (
          priority && <Chip label={priority.label} size="md" radius="7px" color="#c1453f" bg="#fdeaea" border="0" />
        )}
        {!subtask && (
          <Chip
            label={
              <>
                <bdi>
                  {completed} / {countedSubtasks.length}
                </bdi>{' '}
                {t.done}
              </>
            }
            size="md"
            radius="7px"
            color="#6c757b"
            bg="#f1f3f5"
            border="0"
          />
        )}
      </div>
      <div className={styles.navigationSlot} ref={navigationRef} />
      {pager.showSubtaskNav && <ExecutionSubtaskNavigation {...pager} />}
      {review && <ContextNavigation />}
      {review && !subtask && (
        <>
          <ReviewFilterBadges removable disabled={pager.isBusy} />
          <ReviewFilter disabled={pager.isBusy} />
        </>
      )}
      <div className="execution-header-actions" ref={actionsRef} />
    </div>
  )
}
